import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Building2, Users, Briefcase, DollarSign, TrendingUp, Activity, ArrowLeft, Shield
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, token, loading, isSuperUser } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/super-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Super dashboard error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400" />
      </div>
    );
  }

  if (!token || !user) {
    navigate('/signin');
    return null;
  }

  if (!isSuperUser) {
    navigate('/home');
    return null;
  }

  const sub = data?.subscription_breakdown || {};

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-500/20 p-2 rounded-lg">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="super-admin-title">Platform Admin</h1>
              <p className="text-xs text-slate-400">Super Admin Dashboard</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/home')} className="text-slate-300 border-slate-600 hover:bg-slate-700" data-testid="super-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-slate-800" />)}
          </div>
        ) : (
          <>
            {/* Top Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="super-metrics">
              <MetricCard icon={Building2} label="Total Companies" value={data?.total_companies || 0} accent="cyan" />
              <MetricCard icon={DollarSign} label="Monthly Revenue" value={`$${(data?.mrr || 0).toFixed(2)}`} accent="emerald" />
              <MetricCard icon={TrendingUp} label="New Signups (Month)" value={data?.new_signups_this_month || 0} accent="violet" />
              <MetricCard icon={Activity} label="Active Users (7d)" value={data?.active_users_last_7_days || 0} accent="amber" />
            </div>

            {/* Platform Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-base text-slate-200 flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-400" /> Platform Health
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Total Users</span>
                    <span className="text-lg font-bold text-white">{data?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Total Jobs</span>
                    <span className="text-lg font-bold text-white">{data?.total_jobs || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Active Users (7 days)</span>
                    <span className="text-lg font-bold text-cyan-400">{data?.active_users_last_7_days || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-base text-slate-200 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-cyan-400" /> Subscription Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(sub).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-sm text-slate-400 capitalize">{status}</span>
                      <Badge className={getSubBadgeClass(status)}>{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(sub).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No companies yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  const accents = {
    cyan: 'border-cyan-500/30 bg-cyan-500/10',
    emerald: 'border-emerald-500/30 bg-emerald-500/10',
    violet: 'border-violet-500/30 bg-violet-500/10',
    amber: 'border-amber-500/30 bg-amber-500/10'
  };
  const iconColors = {
    cyan: 'text-cyan-400',
    emerald: 'text-emerald-400',
    violet: 'text-violet-400',
    amber: 'text-amber-400'
  };
  return (
    <div className={`rounded-xl border p-4 ${accents[accent]}`}>
      <Icon className={`w-5 h-5 mb-2 ${iconColors[accent]}`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

function getSubBadgeClass(status) {
  const map = {
    trial: 'bg-amber-500/20 text-amber-300',
    active: 'bg-emerald-500/20 text-emerald-300',
    churned: 'bg-red-500/20 text-red-300',
    cancelled: 'bg-red-500/20 text-red-300',
    past_due: 'bg-orange-500/20 text-orange-300'
  };
  return map[status] || 'bg-slate-600 text-slate-300';
}
